import React, { useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useQuery } from "@apollo/client/react";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { ListGroupItem, useListGroupRenderItem } from "../../components/ListGroup";
import { StackScreenProps } from "@react-navigation/stack";
import EmptyScreen from "../../components/EmptyScreen";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { FIND_CLIENT_INFO } from "../../graphql/core";
import NoClientsImage from "../../assets/images/no-clients-image.svg";
import { useDynamicHeaderButton } from "../../hooks/use-dynamic-header-button";
import ScreenCard from "../ScreenCard";
import Button from "../../components/Button";
import { useTheme } from "../../hooks/use-theme";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import { StyleSheet } from "react-native";
import { ClientInfoFieldsFragment } from "../../__generated__/operations/graphql";

type RootParams = StackScreenProps<RootStackParamList>;

const WorkerClientListScreen: React.FC<RootParams> = ({ navigation }) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { loading, error, data, refetch } = useQuery(FIND_CLIENT_INFO, {
        fetchPolicy: "cache-and-network",
        variables: {
            query: "",
        },
    });

    const clientInfoList = useMemo(
        () => data?.findClientInfo.elements || [],
        [data?.findClientInfo],
    );

    const items = useMemo(() => {
        const byName: Record<string, ClientInfoFieldsFragment> = {};
        clientInfoList.forEach((ci: ClientInfoFieldsFragment) => {
            if (ci.name && !byName[ci.name]) {
                byName[ci.name] = ci;
            }
        });
        const nonSortedItems = Object.keys(byName).map((clientName): ListGroupItem<string> => {
            const ci = byName[clientName];
            return {
                value: ci.id,
                title: ci.name,
                avatarPath: ci.logoUrl,
                useBigAvatarOrIcon: true,
                labels: ci.isPrivateClient
                    ? [
                          {
                              text: i18n.t("screens.client_profile.tabs.employees.private_client"),
                          },
                      ]
                    : undefined,
            };
        });
        return nonSortedItems.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""));
    }, [clientInfoList, i18n]);

    const onInviteClient = React.useCallback(() => {
        navigation.navigate("InviteClientInfo");
    }, [navigation]);

    const dynamicButtonProps = useDynamicHeaderButton(
        items.length > 0
            ? {
                  title: i18n.t("screens.user.missions.invite_client"),
                  shortTitle: i18n.t("screens.user.missions.invite_client_short"),
                  onClick: onInviteClient,
                  icon: "add",
              }
            : undefined,
    );

    const renderRow = useListGroupRenderItem(items, { isItemsCheckable: false });

    const isInitialLoading = loading && !data;
    // The populated list is virtualized by ScreenCard; loading/empty/error states keep the
    // default ScrollView path.
    const showList = !error && !isInitialLoading && items.length > 0;

    return (
        <ScreenCard
            refresh={refetch}
            isContentContainerFlex={items.length === 0}
            {...dynamicButtonProps}
            virtualizedItems={showList ? items : undefined}
            renderItem={showList ? renderRow : undefined}
        >
            {showList ? undefined : (
                <LoadingBoundary
                    error={error}
                    loading={isInitialLoading}
                    clearError={() => void refetch()}
                >
                    {items.length === 0 ? (
                        <>
                            <EmptyScreen
                                image={NoClientsImage}
                                text={i18n.t("screens.client_profile.tabs.employees.no_clients")}
                            />
                            <Button
                                size="large"
                                testID="button-invite-client"
                                buttonMode="secondary"
                                style={styles.startButton}
                                onPress={onInviteClient}
                                label={i18n.t("screens.user.missions.invite_client")}
                            />
                        </>
                    ) : null}
                </LoadingBoundary>
            )}
        </ScreenCard>
    );
};

const useStyles = () => {
    const { gridSize } = useTheme();
    const isLarge = useIsScreenSizeMin("large");

    return useMemo(
        () =>
            StyleSheet.create({
                startButton: {
                    marginHorizontal: gridSize,
                    alignSelf: !isLarge ? "stretch" : "center",
                },
            }),
        [gridSize, isLarge],
    );
};

export default WorkerClientListScreen;
